import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setErrorMsg('This reset link is missing its token. Please request a new one.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (pw.length < 8) {
      setErrorMsg('Password must be at least 8 characters.');
      return;
    }
    if (pw !== pw2) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: pw }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Could not reset password.');
      }
      toast.success('Password updated. Please sign in.');
      navigate('/auth', { replace: true });
    } catch (err) {
      setErrorMsg(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-medium">Set a new password</h1>
          <p className="mt-2 text-sm text-muted-foreground">Choose a strong password you'll remember.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errorMsg && (
            <div
              role="alert"
              data-testid="reset-password-error"
              className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
            >
              {errorMsg}
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-sm">New password</Label>
            <div className="relative">
              <Input
                type={show ? 'text' : 'password'}
                value={pw} onChange={(e) => setPw(e.target.value)}
                minLength={8} required className="h-11 rounded-xl pr-11"
                data-testid="reset-password-input"
              />
              <button
                type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="reset-password-toggle"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Confirm new password</Label>
            <Input
              type={show ? 'text' : 'password'}
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              minLength={8} required className="h-11 rounded-xl"
              data-testid="reset-password-confirm-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full h-11 rounded-full font-medium"
            disabled={loading || !token}
            data-testid="reset-password-submit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update password'}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <Link to="/auth" className="text-primary font-medium hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
